// Talk-to-Sales booking: resolves which rep the visitor last met with in
// HubSpot, then embeds that rep's real HubSpot meetings page as an iframe.

// Generic proxy: forwards { endpoint, method, payload } to HubSpot.
const HUBSPOT_PROXY_API =
  'https://mfbac4ks6wyz3rupovwxavtjuu0knskb.lambda-url.us-east-1.on.aws/';

// Meeting slugs need manual maintenance (HubSpot has no concept of a
// personal booking-page slug); owner-id -> name resolution is automatic.
export const SALES_REPS_BY_NAME = {
  'rachit nanda': 'rachit-nanda/upgrade',
  'surbhi kohli': 'surbhi1/upgrade',
  'nikhil saini': 'nikhil-saini/upgrade',
};

// Fallback round-robin link when no specific rep can be resolved.
export const GLOBAL_FALLBACK_SLUG = 'imagine-io/book-a-meeting';

const normalize = (value = '') => value?.trim()?.toLowerCase();

// GET requests must omit "payload" entirely - including it (even as {})
// causes intermittent 500s from this Lambda.
async function callHubspotProxy({ endpoint, method = 'GET', payload }) {
  const body = method === 'GET' ? { endpoint, method } : { endpoint, method, payload };

  const response = await fetch(HUBSPOT_PROXY_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const text = await response.text().catch(() => '');
  let result = null;
  try {
    result = text ? JSON.parse(text) : null;
  } catch (error) {
    throw new Error(`Bad JSON from ${endpoint}: ${text.slice(0, 200)}`);
  }

  // Some failure modes return HTTP 200 with an error body.
  if (!response.ok || result?.error) {
    throw new Error(
      result?.error || result?.message || `HubSpot request failed (${response.status}) on ${endpoint}`
    );
  }
  return result;
}

// Custom contact property, not a HubSpot default.
const OWNER_OF_LAST_MEETING_BOOKED_PROPERTY = 'owner_of_last_meeting_booked';

// Owner id -> { name, email }, cached per session.
let cachedOwnerMap = null;

async function loadOwnerMap() {
  if (cachedOwnerMap) return cachedOwnerMap;

  try {
    const result = await callHubspotProxy({ endpoint: '/crm/v3/owners?limit=100', method: 'GET' });
    const map = {};
    (result?.results || []).forEach((owner) => {
      const name = [owner?.firstName, owner?.lastName].filter(Boolean).join(' ');
      map[owner?.id] = { name, email: owner?.email };
    });
    cachedOwnerMap = map;
  } catch (error) {
    console.error('loadOwnerMap error:', error);
    cachedOwnerMap = {};
  }
  return cachedOwnerMap;
}

// Resolves the visitor's last-meeting-owner to a rep slug, or null to fall
// back to the Global link (no contact, no history, or unknown owner).
export async function resolveRepMeetingSlug(email) {
  if (!email) return null;

  try {
    const searchResult = await callHubspotProxy({
      endpoint: '/crm/v3/objects/contacts/search',
      method: 'POST',
      payload: {
        filterGroups: [
          { filters: [{ propertyName: 'email', operator: 'EQ', value: email }] },
        ],
        properties: ['email', OWNER_OF_LAST_MEETING_BOOKED_PROPERTY],
        limit: 1,
      },
    });

    const contact = searchResult?.results?.[0];
    const lastMeetingOwnerId = contact?.properties?.[OWNER_OF_LAST_MEETING_BOOKED_PROPERTY];
    if (!lastMeetingOwnerId) return null;

    const ownerMap = await loadOwnerMap();
    const owner = ownerMap[lastMeetingOwnerId];
    if (!owner?.name) return null;

    return SALES_REPS_BY_NAME[normalize(owner.name)] || null;
  } catch (error) {
    console.error('resolveRepMeetingSlug error:', error);
    return null;
  }
}

// Some accounts have a literal "undefined"/"null" string stored instead of a
// real value - treat those as missing rather than pre-filling garbage.
const isRealValue = (value) =>
  Boolean(value) && !['undefined', 'null'].includes(normalize(String(value)));

// No company param: the direct link doesn't ask for one, and passing it
// risked overwriting the contact's real HubSpot company value.
export function buildMeetingEmbedUrl({ slug, firstName, lastName, email }) {
  const params = new URLSearchParams({ embed: 'true' });
  if (isRealValue(firstName)) {
    params.set('firstname', firstName);
    params.set('firstName', firstName);
  }
  if (isRealValue(lastName)) {
    params.set('lastname', lastName);
    params.set('lastName', lastName);
  }
  if (isRealValue(email)) {
    params.set('email', email);
  }
  return `https://meetings.hubspot.com/${slug}?${params.toString()}`;
}
